import type { ProductDetailsPage } from "@decocms/apps-commerce/types"
import { createCachedLoaderCf } from "~/sdk/cachedLoaderCf"
import clsx from "~/sdk/clx"

export interface Props {
    /**
     * @title Integração
     */
    page: ProductDetailsPage | null
    /**
     * Posts do blog (preenchido pelo loader no server).
     */
    posts?: BlogPost[]
}

const BLOG_API = 'https://blog.miess.com.br/wp-json/wp/v2/posts'
const POSTS_LIMIT = 6

/** Resposta bruta da API WordPress (com _embed). */
export interface WPPost {
    id: number
    title: { rendered: string }
    excerpt: { rendered: string }
    link: string
    featured_image_src_large?: [string, number, number, boolean]
    yoast_head_json?: { og_image?: Array<{ url: string }> }
    _embedded?: {
        'wp:featuredmedia'?: Array<{
            source_url: string
            alt_text?: string
            media_details?: {
                sizes?: {
                    medium?: { source_url: string }
                }
            }
        }>
    }
}

/** Post normalizado pelo loader para renderização. */
export interface BlogPost {
    id: number
    title: string
    excerpt: string
    link: string
    imageUrl: string
}

function stripHtml(html: string): string {
    if (typeof document !== 'undefined') {
        const div = document.createElement('div')
        div.innerHTML = html
        return div.textContent?.trim() ?? ''
    }
    return html.replace(/<[^>]*>/g, '').trim()
}

export function getPostImageUrl(post: WPPost): string {
    const media = post._embedded?.['wp:featuredmedia']?.[0]
    return (
        media?.media_details?.sizes?.medium?.source_url ??
        media?.source_url ??
        post.featured_image_src_large?.[0] ??
        post.yoast_head_json?.og_image?.[0]?.url ??
        ''
    )
}

function normalizePost(post: WPPost): BlogPost {
    return {
        id: post.id,
        title: stripHtml(post.title.rendered),
        excerpt: stripHtml(post.excerpt.rendered),
        link: post.link,
        imageUrl: getPostImageUrl(post),
    }
}

function BlogCard({
    title,
    excerpt,
    link,
    imageUrl,
    className: cardClass,
}: {
    title: string
    excerpt: string
    link: string
    imageUrl: string
    className?: string
}) {
    return (
        <a
            href={link}
            target='_blank'
            rel='noopener noreferrer'
            className={clsx(
                'flex shrink-0 flex-col gap-2 text-left w-full',
                cardClass ?? 'w-[260px]',
            )}
        >
            <div className='relative h-[165px] w-full shrink-0 overflow-hidden rounded-lg'>
                {imageUrl
                    ? (
                        // Plain <img>, not CustomImage/Image: both route through
                        // decoims.com/image, which only serves workspace-relative
                        // assets and answers 403 for any absolute external URL.
                        // The Deno app could optimize these because its
                        // `/live/invoke/website/loaders/image.ts` proxy accepted
                        // arbitrary origins; that route doesn't exist here, so the
                        // WordPress URL is used as-is (the loader already picks the
                        // `medium` 300px rendition, so it isn't oversized).
                        <img
                            src={imageUrl}
                            alt={title}
                            width={260}
                            height={165}
                            className='object-cover size-full'
                            loading='lazy'
                        />
                    )
                    : (
                        <div
                            className='size-full bg-neutral-200'
                            aria-hidden
                        />
                    )}
            </div>
            <h3 className='line-clamp-2 font-semibold leading-tight text-[#91153a] text-sm'>
                {title}
            </h3>
            <p className='line-clamp-3 min-h-[60px] font-normal leading-normal text-[#820652] text-sm'>
                {excerpt}
            </p>
            <span className='inline-flex h-6 items-center gap-1 font-semibold text-[#91153a] text-sm'>
                Leia mais
                <svg
                    className='size-4 shrink-0'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                    aria-hidden
                >
                    <path
                        d='M6 12L10 8L6 4'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                    />
                </svg>
            </span>
        </a>
    )
}

function HeartIcon({ className }: { className?: string }) {
    return (
        <svg
            className={className ?? 'size-[18px] shrink-0'}
            width='18'
            height='18'
            viewBox='0 0 18 18'
            fill='none'
            aria-hidden
        >
            <path
                d='M9 15.5L7.5 14.2C3.5 10.5 1 8.2 1 5.5C1 3.02 3.02 1 5.5 1C6.92 1 8.28 1.7 9 2.82C9.72 1.7 11.08 1 12.5 1C14.98 1 17 3.02 17 5.5C17 8.2 14.5 10.5 10.5 14.2L9 15.5Z'
                stroke='#91153a'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
                fill='none'
            />
        </svg>
    )
}

/** Skeleton de um card do blog (imagem 260x165, 3 linhas de texto, linha "Leia mais") */
function BlogCardSkeleton({ className }: { className?: string }) {
    return (
        <div
            className={clsx(
                'flex shrink-0 flex-col gap-2 text-left w-full animate-pulse',
                className ?? 'w-[260px]',
            )}
        >
            <div className='h-[165px] w-full shrink-0 rounded-lg bg-neutral-200' />
            <div className='h-8 w-4/5 rounded bg-neutral-200' />
            <div className='min-h-[60px] flex flex-col gap-2'>
                <div className='h-3 w-full rounded bg-neutral-200' />
                <div className='h-3 w-full rounded bg-neutral-200' />
                <div className='h-3 w-3/4 rounded bg-neutral-200' />
            </div>
            <div className='h-6 w-24 rounded bg-neutral-200' />
        </div>
    )
}

/**
 * SWR de 5min + single-flight + stale-if-error sobre o fetch do WordPress
 * (profile `static` → loader.fresh 300s / sie 1800s, cacheHeaders.ts:78-84).
 *
 * Sem isto o blog era consultado a cada render de PDP — um fetch externo
 * bloqueante no SSR de toda página de produto que tenha "Tag ID Blog".
 *
 * Por que o cache fica AQUI e não em `export const cache` na section:
 *   - a chave do cache de section é `hash(props CRUAS)` (sectionLoaders.ts:126)
 *     e as props desta section carregam a `ProductDetailsPage` inteira. Daria
 *     uma entrada por produto (e por variante de skuId) guardando o page object
 *     completo, num cache global de 200 entradas SEM teto de bytes
 *     (MAX_SECTION_CACHE_ENTRIES, sectionLoaders.ts:117);
 *   - o fetch depende de UM input: a tag. Chaveado por ela o cache é minúsculo
 *     e compartilhado por todos os produtos da mesma tag.
 */
const fetchPostsByTag = createCachedLoaderCf(
    'miess/blogPostsByTag',
    async ({ tag }: { tag: string }): Promise<BlogPost[]> => {
        const url = `${BLOG_API}?tags=${encodeURIComponent(tag)}&per_page=${POSTS_LIMIT}&_embed`
        const res = await fetch(url)
        const raw = (await res.json()) as WPPost[]
        return raw.slice(0, POSTS_LIMIT).map(normalizePost)
    },
    'static',
)

/** Loader: busca posts do WordPress no server usando o Tag ID Blog do produto. */
export async function loader(
    props: Props,
    _req: Request,
): Promise<Props & { posts: BlogPost[] }> {
    const posts: BlogPost[] = []
    if (!props.page?.product) {
        return { ...props, posts }
    }
    const additionalProperties =
        props.page.product.isVariantOf?.additionalProperty ?? []
    const tagIdBlog = additionalProperties.find(
        (prop) => prop.name?.trim().toLowerCase() === 'tag id blog',
    )
    const tagValue = tagIdBlog?.value
    if (!tagValue) {
        return { ...props, posts }
    }
    try {
        return { ...props, posts: await fetchPostsByTag({ tag: tagValue }) }
    } catch (_err) {
        // Falha não é cacheada: a próxima PDP tenta de novo.
        return { ...props, posts }
    }
}

/** Loading fallback com skeleton no layout desktop e mobile (Deco: https://docs.deco.cx/pt/developing-capabilities/sections/loading-fallback) */
export function LoadingFallback() {
    const cardCount = 6

    return (
        <section className='w-full pt-12 pb-12' data-product-blog-posts-skeleton>
            <div className='mx-auto flex max-w-[1770px] flex-col gap-12 px-4'>
                {/* Header skeleton: hearts + título */}
                <div className='flex items-center justify-center gap-4 px-4'>
                    <div className='size-[18px] shrink-0 rounded bg-neutral-200 animate-pulse' />
                    <div className='h-10 w-64 rounded bg-neutral-200 animate-pulse' />
                    <div className='size-[18px] shrink-0 rounded bg-neutral-200 animate-pulse' />
                </div>

                {/* Cards: mobile = scroll 75vw por card; desktop = grid 260px */}
                <div
                    className={clsx(
                        'flex gap-6',
                        'overflow-x-auto overflow-y-hidden snap-x snap-mandatory scrollbar-none',
                        'lg:flex-wrap lg:overflow-visible lg:justify-between',
                    )}
                >
                    {Array.from({ length: cardCount }).map((_, i) => (
                        <div
                            key={i}
                            className={clsx(
                                'shrink-0 snap-start',
                                'w-[75vw] min-w-[75vw] lg:w-[calc(100%/6-32px)] lg:min-w-0',
                            )}
                        >
                            <BlogCardSkeleton className='w-full' />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    )
}

export default function ProductBlogPosts({
    page,
    posts = [],
}: Props & { posts?: BlogPost[] }) {
    if (!page || !posts.length) {
        return null
    }

    return (
        <section className='w-full pt-12 pb-12' data-product-blog-posts>
            <div className='mx-auto flex max-w-[1770px] flex-col gap-12 px-4'>
                {/* Header: heart + "Explorando Sensações" + heart */}
                <div className='flex items-center justify-center gap-4 px-4'>
                    <HeartIcon />
                    <h2 className='text-center font-light uppercase leading-10 text-[#91153a] text-4xl'>
                        Explorando{' '}
                        <span className='font-bold text-salmon'>Sensações</span>
                    </h2>
                    <HeartIcon />
                </div>

                {/* Mobile: scroll horizontal (1 card + 1/3 do próximo). Desktop: grid. Render server-side. */}
                <div
                    className={clsx(
                        'flex gap-6',
                        'overflow-x-auto overflow-y-hidden snap-x snap-mandatory scroll-smooth scrollbar-none',
                        'lg:overflow-visible lg:justify-between',
                    )}
                    style={{ WebkitOverflowScrolling: 'touch' }}
                >
                    {posts.map((post) => (
                        <div
                            key={post.id}
                            className={clsx(
                                'shrink-0 snap-start snap-always',
                                'w-[75vw] min-w-[75vw] lg:w-[calc(100%/6-32px)] lg:min-w-0',
                            )}
                        >
                            <BlogCard
                                title={post.title}
                                excerpt={post.excerpt || post.title}
                                link={post.link}
                                imageUrl={post.imageUrl}
                                className='w-full'
                            />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    )
}
